'use client';

import Link from '~/(tenant)/_components/tenant-link';

import { useQuery } from '@tanstack/react-query';

import { PersonalAccountDropdown } from '@kit/accounts/personal-account-dropdown';
import { useSignOut } from '@kit/supabase/hooks/use-sign-out';
import { useSupabase } from '@kit/supabase/hooks/use-supabase';
import { JWTUserData } from '@kit/supabase/types';
import { Trans } from '@kit/ui/trans';

import { UserAccountDropdown } from '~/(tenant)/_components/user-account-dropdown';
import featuresFlagConfig from '~/config/feature-flags.config';
import pathsConfig from '~/config/paths.config';

const paths = {
  home: pathsConfig.app.home,
  bookCourt: '/membership',
};

const features = {
  enableThemeToggle: featuresFlagConfig.enableThemeToggle,
};

interface OiaHeaderAccountSectionProps {
  onNavigate?: () => void;
  isOpaque?: boolean;
}

export function OiaHeaderAccountSection({
  onNavigate,
  isOpaque = true,
}: OiaHeaderAccountSectionProps) {
  const session = useSession();
  const userRole = useUserRole(session.data?.user?.id);
  const teamAccount = useTeamAccount(session.data?.user?.id);
  const signOut = useSignOut({
    onSuccess: () => window.location.reload(),
  });

  if (session.data) {
    const role = userRole.data ?? 'guest';

    if (role === 'owner' || role === 'member') {
      return (
        <div className="animate-in fade-in flex items-center gap-x-3 duration-500">
          <Link
            href={paths.bookCourt}
            onClick={onNavigate}
            className="font-oia-eyebrow inline-flex h-10 items-center justify-center rounded-full bg-[var(--oia-ink)] px-6 text-[11px] text-[var(--oia-cream)] transition-opacity duration-200 hover:opacity-90"
          >
            <Trans i18nKey="landing:nav.apply" defaults="APPLY" />
          </Link>

          <Link
            href={
              teamAccount.data
                ? `/home/${teamAccount.data.slug}/dashboard`
                : '/home/settings'
            }
            onClick={onNavigate}
            className={
              isOpaque
                ? 'font-oia-eyebrow hidden h-10 items-center justify-center rounded-full border border-[var(--oia-ink)]/40 px-6 text-[11px] text-[var(--oia-ink)] transition-colors duration-200 hover:bg-[var(--oia-ink)]/5 md:inline-flex'
                : 'font-oia-eyebrow hidden h-10 items-center justify-center rounded-full border border-white/50 px-6 text-[11px] text-white transition-colors duration-200 hover:bg-white/10 md:inline-flex'
            }
          >
            <Trans i18nKey="common:adminPanel" defaults="Admin Panel" />
          </Link>

          <PersonalAccountDropdown
            showProfileName={false}
            paths={paths}
            features={features}
            user={session.data.user as unknown as JWTUserData}
            signOutRequested={() => signOut.mutateAsync()}
            className="z-50"
          />
        </div>
      );
    }

    if (role === 'user') {
      return (
        <div className="animate-in fade-in flex items-center gap-x-3 duration-500">
          <Link
            href={paths.bookCourt}
            onClick={onNavigate}
            className="font-oia-eyebrow inline-flex h-10 items-center justify-center rounded-full bg-[var(--oia-ink)] px-6 text-[11px] text-[var(--oia-cream)] transition-opacity duration-200 hover:opacity-90"
          >
            <Trans i18nKey="landing:nav.apply" defaults="APPLY" />
          </Link>

          <UserAccountDropdown
            showProfileName={false}
            features={features}
            user={session.data.user as unknown as JWTUserData}
            signOutRequested={() => signOut.mutateAsync()}
            className="z-50"
          />
        </div>
      );
    }
  }

  return <GuestActions onNavigate={onNavigate} isOpaque={isOpaque} />;
}

function GuestActions({
  onNavigate,
  isOpaque = true,
}: {
  onNavigate?: () => void;
  isOpaque?: boolean;
}) {
  return (
    <div className="animate-in fade-in flex items-center gap-x-3 duration-500">
      <Link
        href="/membership"
        onClick={onNavigate}
        className="font-oia-eyebrow inline-flex h-10 items-center justify-center rounded-full bg-[var(--oia-ink)] px-6 text-[11px] text-[var(--oia-cream)] transition-opacity duration-200 hover:opacity-90"
      >
        <Trans i18nKey="landing:nav.apply" defaults="APPLY" />
      </Link>
      {/* Use <a> tag for hard navigation to avoid hydration mismatch between route groups */}
      <a
        href={pathsConfig.auth.signIn}
        onClick={onNavigate}
        className={
          isOpaque
            ? 'font-oia-eyebrow inline-flex h-10 items-center justify-center rounded-full border border-[var(--oia-ink)]/40 px-6 text-[11px] text-[var(--oia-ink)] transition-colors duration-200 hover:bg-[var(--oia-ink)]/5'
            : 'font-oia-eyebrow inline-flex h-10 items-center justify-center rounded-full border border-white/60 px-6 text-[11px] text-white transition-colors duration-200 hover:bg-white/10'
        }
      >
        <Trans i18nKey="auth:signIn" defaults="Sign In" />
      </a>
    </div>
  );
}

function useSession() {
  const client = useSupabase();

  return useQuery({
    queryKey: ['session'],
    queryFn: async () => {
      const { data } = await client.auth.getSession();
      return data.session;
    },
  });
}

function useUserRole(userId?: string) {
  const client = useSupabase();

  return useQuery({
    queryKey: ['user-role', userId],
    queryFn: async () => {
      if (!userId) return 'guest';

      try {
        const { data: memberships } = await client
          .from('accounts_memberships')
          .select('account_role')
          .eq('user_id', userId);

        if (memberships && memberships.length > 0) {
          if (memberships.some((m) => m.account_role === 'owner')) {
            return 'owner';
          }
          return 'member';
        }

        return 'user';
      } catch {
        return 'guest';
      }
    },
    enabled: !!userId,
  });
}

function useTeamAccount(userId?: string) {
  const client = useSupabase();

  return useQuery({
    queryKey: ['team-account', userId],
    queryFn: async () => {
      if (!userId) return null;

      try {
        const { data: memberships } = await client
          .from('accounts_memberships')
          .select(
            `
            account_id,
            accounts!accounts_memberships_account_id_fkey!inner (
              id,
              slug,
              name,
              is_personal_account
            )
          `,
          )
          .eq('user_id', userId)
          .eq('accounts.is_personal_account', false);

        if (memberships && memberships.length > 0) {
          return memberships[0]?.accounts;
        }

        return null;
      } catch {
        return null;
      }
    },
    enabled: !!userId,
  });
}
